import React from 'react';
import { PlayerSize } from '../../components/player/player';
import SyncType from '../../models/playlist/syncType';
import RepeatState from '../../models/repeatState';
import { StorageProviderMap } from '../../models/storageProviderKeys';
import SyncProvider from '../../models/synchronization/syncProvider';
import SyncProviders from '../../models/synchronization/syncProviders';
import { reqQueueCreator } from '../queues/reqQueue';
import IStorageProvider from '../storage/istorageProvider';

/**
 * gzip codec for the app-data files in cloud storage.
 *
 * The method names are wasm-flate's, kept so the many call sites that just
 * pass this object around did not have to change. It is now backed by fflate
 * (pure JS). wasm-flate needed WebAssembly, which is absent under iOS
 * Lockdown Mode - and because it gated startup, the app never finished loading
 * at all there. It no longer gates anything: fflate ships in the bundle.
 *
 * Both emit ordinary gzip streams, so files written by either are readable by
 * the other.
 */
export interface Flate {
  gzip_encode_raw(bytes: Uint8Array): Uint8Array;
  gzip_decode_raw(bytes: Uint8Array): Uint8Array;
}

export class SettingsState {
  repeatState = RepeatState.All;
  libraryPath?: string;
  showPlaylistStatusBar = false;
  confirmDelete = true;
  enableGoogleDriveIdNavigation = false;
  /**
   * Allows the user to lock player size as large despite switching
   * to track types with smaller default player sizes
   */
  lockPlayerSizeMedium = false;

  /**
   * Guess the DJ from a Mixcloud show title when the account that posted it
   * is not the one who played. Off by default: it is the only guessing
   * Fadila does, and a guess belongs to whoever asked for it.
   *
   * models/track/mixcloudArtistFromTitle.ts, and docs/MIXCLOUD.md 4c.
   */
  mixcloudArtistFromTitle = false;

  /**
   * The Mixcloud account whose playlists and favourites the Mixcloud page
   * shows on the right.
   *
   * A username rather than a session because Mixcloud sign-in is not
   * available to us: its OAuth authorize endpoint answers 404
   * (docs/MIXCLOUD.md 4d). Everything the right-hand column needs is public,
   * so the only thing missing without a login is Mixcloud telling us who you
   * are - and this is that, typed once. If signing in ever works, it fills
   * this in and nothing else has to change.
   */
  mixcloudUsername?: string;

  /**
   * Using this constructor helps upgrading older settings.js when we make additions
   * and sets sensible defaults when applicable.
   *
   * @param state
   */
  constructor(state?: SettingsState) {
    if (!state) {
      return;
    }

    // `??`, not `||`. The old form could not restore any value JavaScript
    // calls falsy, which quietly broke four settings that look fine in the
    // UI until the next load: RepeatState.None is 0, so repeat-off came back
    // as repeat-all; confirmDelete defaults true, so switching it off came
    // back on; and two settings below were never copied at all, so they reset
    // on every load however they were left.
    this.repeatState = state.repeatState ?? this.repeatState;
    this.libraryPath = state.libraryPath;
    this.showPlaylistStatusBar = state.showPlaylistStatusBar ?? this.showPlaylistStatusBar;
    this.confirmDelete = state.confirmDelete ?? this.confirmDelete;
    this.enableGoogleDriveIdNavigation =
      state.enableGoogleDriveIdNavigation ?? this.enableGoogleDriveIdNavigation;
    this.lockPlayerSizeMedium = state.lockPlayerSizeMedium ?? this.lockPlayerSizeMedium;
    this.mixcloudArtistFromTitle = state.mixcloudArtistFromTitle ?? this.mixcloudArtistFromTitle;
    this.mixcloudUsername = state.mixcloudUsername;
    // explainGoogleRenewalPopup, 2026-09 to 2026-10, is read no more: the
    // window it explained is gone with the implicit flow. A stored value is
    // ignored rather than migrated.
  }
}

export class DeviceCapabilities {
  detectionComplete = false;

  hasServiceWorkers = false;
  hasLocalStorage = false;

  hasFetch = false;
  hasPromise = false;
  hasTextEncoder = false;
}
export interface GlobalState {
  deviceCapabilities: DeviceCapabilities;

  settingsLoaded: boolean;

  playerSize: PlayerSize;
  setPlayerSize: React.Dispatch<React.SetStateAction<PlayerSize>>;

  toggleRepeat(): void;

  callSnackbar(message: any, error?: boolean): void;

  syncProviders: SyncProviders;
  addSyncProvider(syncType: SyncType, syncProvider: SyncProvider): void;

  flate: Flate;

  storageProvider: React.RefObject<IStorageProvider | undefined>;
  /**
   * Configures the chosen storage provider
   * @param key storageProviderMap key
   */
  configureStorageProvider(key?: string): void;
  /**
   * Providers register with a specific key,
   * key matches saved storage provider choice in localStorage allowing fast app load.
   */
  storageProviderMap: StorageProviderMap;
  autoConfigureStorageProvider(): void;

  isProcessing: boolean;
  setRunningTask(task: string): void;
  removeRunningTask(task: string | number): void;

  /**
   * Music library path in cloud storage
   * Base path used when f.x. saving podcasts, a path of /Music would then save to
   * /Music/Podcasts/[name of podcast]/
   */
  libraryPath?: string;
  setLibraryPath(path: string): void;

  unifiedReqQueue: ReturnType<typeof reqQueueCreator>;

  /**
   * Object store for dnd operations, key/value store
   * Dnd should cleanup but to ensure correct data retrieval
   * we access dnd data by timestamp key
   */
  dndData: any;

  /**
   * Service provider logout handler
   */
  spLogoutHandling: () => Promise<void>;
  /**
   * Allows library to configure it's logout cleanup methods
   * Those are used in conjunction with context cleanup methods when spLogoutHandling
   * is called by cloud providers
   * @param f
   */
  setLibrarySpLogoutHandling(f: () => Promise<void>): void;

  scrollbarWidth: number | undefined;

  toggleShowPlaylistStatusBar(): void;

  toggleConfirmDelete(): void;
  toggleGoogleDriveIdNavigation(): void;
  toggleLockPlayerSizeMedium(): void;
  toggleMixcloudArtistFromTitle(): void;
  setMixcloudUsername(username: string | undefined): void;

  settings: SettingsState;
}

export const Context = React.createContext({} as GlobalState);
